// @givmo/crm — the segment builder's condition rows, and their one mapping to the wire (CRM-5).
//
// A row is one plain sentence: "<field> <is | is not> <value>", or for the last gift date "<is
// before | is after> <date>", "is between <date> and <date>" and "is in the last <N> <days | months
// | years>". The builder offers five fields — tag, source, relationship, email address and the last
// gift date (the contact's own, whichever kind it is: an imported date, or a Givmo donor's last
// shared gift) — each with one value, and every row must hold. A saved row of the older rule that
// reads imported dates alone (EMC-BE10, `imported_last_gift_date`) stays that rule, so a saved
// segment never changes meaning; no new row is offered it. The wire carries more than the builder can write
// (several values in a condition, `all` of some tags, an import batch): a saved definition this
// builder cannot write back EXACTLY reads as `null` here, and the screen then keeps it untouched and
// offers the rename alone, so editing a segment can never quietly change what it means.

import { GIFT_WINDOW_MAX } from './crm-decode';
import type {
  CrmContactSource,
  CrmGiftWindowUnit,
  CrmSegmentCondition,
  CrmSegmentDefinition,
} from './wire-types';

export type ConditionField =
  | 'tag'
  | 'source'
  | 'relationship'
  | 'has_email'
  | 'last_gift_date'
  | 'imported_last_gift_date';
export type ConditionOp = 'is' | 'is_not' | 'before' | 'after' | 'between' | 'within';

/** The two date fields: the contact's own last gift, and the older imported-only rule. */
export function isDateField(field: ConditionField): boolean {
  return field === 'last_gift_date' || field === 'imported_last_gift_date';
}

/** The source values a row offers: a Givmo donor, or a record the organization supplied. */
export const SOURCE_FEED = 'feed';
export const SOURCE_SUPPLIED = 'supplied';
/** The email-address values a row offers. */
export const EMAIL_ON_FILE = 'on_file';
export const EMAIL_MISSING = 'missing';

export interface ConditionDraft {
  /** A stable key for the row while it is edited; never sent. */
  key: string;
  field: ConditionField;
  op: ConditionOp;
  /**
   * The one value (a `YYYY-MM-DD` date for the last gift date; for "is in the last", the number as
   * typed); empty while the row has none yet.
   */
  value: string;
  /** The second date of "is between"; empty or absent while it has none. */
  end?: string;
  /** The unit of "is in the last". */
  unit?: CrmGiftWindowUnit;
}

/** The number typed for "is in the last", when it is a whole number the window takes. */
function windowAmount(draft: ConditionDraft): number | null {
  if (!/^[1-9][0-9]*$/.test(draft.value) || draft.unit === undefined) return null;
  const amount = Number(draft.value);
  return amount <= GIFT_WINDOW_MAX[draft.unit] ? amount : null;
}

const SUPPLIED: readonly CrmContactSource[] = ['import', 'manual'];

let nextKey = 0;
export function draftKey(): string {
  nextKey += 1;
  return `condition-${nextKey}`;
}

/** Whether every row names its value (both dates for "is between"), so it can be saved. */
export function draftsComplete(drafts: readonly ConditionDraft[]): boolean {
  return drafts.every((draft) => conditionOf(draft) !== null);
}

/** A picked calendar day (a local-midnight `Date`) as `YYYY-MM-DD`, from its LOCAL parts. */
export function calendarDayString(day: Date): string {
  const year = String(day.getFullYear()).padStart(4, '0');
  const month = String(day.getMonth() + 1).padStart(2, '0');
  const date = String(day.getDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

/** A `YYYY-MM-DD` date as the local-midnight `Date` a date picker shows, or undefined. */
export function calendarDay(value: string | undefined): Date | undefined {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(value ?? '');
  if (match === null) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function conditionOf(draft: ConditionDraft): CrmSegmentCondition | null {
  if (draft.value.length === 0) return null;
  switch (draft.field) {
    case 'tag':
      return { field: 'tag', op: draft.op === 'is' ? 'any' : 'none', values: [draft.value] };
    case 'relationship':
      return {
        field: 'relationship',
        op: draft.op === 'is' ? 'any' : 'none',
        values: [draft.value],
      };
    case 'source':
      return {
        field: 'source',
        op: draft.op === 'is_not' ? 'is_not' : 'is',
        values: draft.value === SOURCE_FEED ? ['feed'] : [...SUPPLIED],
      };
    case 'has_email': {
      const onFile = draft.value === EMAIL_ON_FILE;
      return { field: 'has_email', op: 'is', value: draft.op === 'is' ? onFile : !onFile };
    }
    case 'last_gift_date':
    case 'imported_last_gift_date': {
      const field = draft.field;
      if (draft.op === 'within') {
        const amount = windowAmount(draft);
        return field === 'last_gift_date' && amount !== null && draft.unit !== undefined
          ? { field, op: 'within', amount, unit: draft.unit }
          : null;
      }
      if (draft.op === 'before' || draft.op === 'after') {
        return { field, op: draft.op, value: draft.value };
      }
      const end = draft.end ?? '';
      if (draft.op !== 'between' || end.length === 0) return null;
      // Between two dates means the same in either order; the wire wants the earlier first.
      const [first, last] = draft.value <= end ? [draft.value, end] : [end, draft.value];
      return { field, op: 'between', value: first, end: last };
    }
  }
}

/** The wire definition the rows say. A row without a value says nothing yet and is left out. */
export function definitionFromDrafts(drafts: readonly ConditionDraft[]): CrmSegmentDefinition {
  return {
    v: 1,
    all: drafts.map(conditionOf).filter((row): row is CrmSegmentCondition => row !== null),
  };
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value) => right.includes(value));
}

function draftOf(condition: CrmSegmentCondition): ConditionDraft | null {
  switch (condition.field) {
    case 'tag':
      if (condition.values.length !== 1 || condition.op === 'all') return null;
      return {
        key: draftKey(),
        field: 'tag',
        op: condition.op === 'any' ? 'is' : 'is_not',
        value: condition.values[0] ?? '',
      };
    case 'relationship':
      if (condition.values.length !== 1) return null;
      return {
        key: draftKey(),
        field: 'relationship',
        op: condition.op === 'any' ? 'is' : 'is_not',
        value: condition.values[0] ?? '',
      };
    case 'source':
      if (sameSet(condition.values, ['feed'])) {
        return { key: draftKey(), field: 'source', op: condition.op, value: SOURCE_FEED };
      }
      if (sameSet(condition.values, SUPPLIED)) {
        return { key: draftKey(), field: 'source', op: condition.op, value: SOURCE_SUPPLIED };
      }
      return null;
    case 'has_email':
      return {
        key: draftKey(),
        field: 'has_email',
        op: 'is',
        value: condition.value ? EMAIL_ON_FILE : EMAIL_MISSING,
      };
    case 'import_batch':
      // The builder offers no import to choose until the import's own list is served.
      return null;
    case 'imported_last_gift_date':
    case 'last_gift_date':
      if (condition.op === 'within') {
        return {
          key: draftKey(),
          field: 'last_gift_date',
          op: 'within',
          value: String(condition.amount),
          unit: condition.unit,
        };
      }
      return condition.op === 'between'
        ? {
            key: draftKey(),
            field: condition.field,
            op: 'between',
            value: condition.value,
            end: condition.end,
          }
        : { key: draftKey(), field: condition.field, op: condition.op, value: condition.value };
  }
}

/**
 * The rows a saved definition reads as, or null when this builder could not write it back exactly
 * (or the definition itself was unreadable).
 */
export function draftsFromDefinition(
  definition: CrmSegmentDefinition | null,
): ConditionDraft[] | null {
  if (definition === null) return null;
  const drafts = definition.all.map(draftOf);
  return drafts.some((draft) => draft === null) ? null : (drafts as ConditionDraft[]);
}

/** A slug of the class profile's taxonomy in plain words ("grantmaker" → "Grantmaker"). */
export function relationshipLabel(slug: string): string {
  const words = slug.replace(/[_-]+/g, ' ').trim();
  return words.length === 0 ? slug : words.charAt(0).toUpperCase() + words.slice(1);
}
